/**
 * What: Runtime transport contract for Shopify setup-packet history responses.
 * Transport / data-flow: Web shapes persisted packet rows; Shopify validates the complete HTTP response before merging it locally.
 *
 */
import * as z from 'zod';
import { shopifyTreatmentMetadataSchema } from './shopify-treatment-metadata.js';
import { shopifyStudyNodeProgressSchema } from './shopify-study-node-progress.js';
/**
 * The result of a finished run, for the Tests table (#4370). Only the decision key, never the winner or scores.
 * - `decision`: a scored run's decision key (for example `none_viable`). A string, not an enum, for the same reason as
 *   the results contract: stored decisions can predate the current scorer union. The app maps unknown keys to
 *   "Result unavailable".
 * - `not_scored`: a run whose methodology writes no decision (survey runs).
 * - `unavailable`: a scored run whose decision could not be read, or was past the per-request read limit.
 */
export const shopifySetupPacketRunResultSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('decision'), decision: z.string().min(1) }),
    z.object({ kind: z.literal('not_scored') }),
    z.object({
        kind: z.literal('unavailable'),
        reason: z.enum(['decision_missing', 'decision_unreadable', 'not_loaded']),
    }),
]);
/** Legacy camelCase run shape used by the setup history response. */
export const shopifySetupPacketHistoryLatestRunSchema = z.object({
    id: z.string(),
    status: z.enum(['queued', 'running', 'completed', 'failed', 'cancelled', 'superseded']),
    latestStepKey: z.string().nullable(),
    reportReady: z.boolean(),
    requestedAt: z.string(),
    finishedAt: z.string().nullable(),
    model: z.string().nullable(),
    /** The run's one-word result for the Tests table (#4370); `null` until the run has a report. */
    result: shopifySetupPacketRunResultSchema.nullable(),
});
const shopifySetupPacketHistoryEntryBaseSchema = z.object({
    packetId: z.string(),
    packetStatus: z.enum(['setup_prepared', 'study_draft_created', 'failed']),
    runId: z.string().nullable(),
    productId: z.string(),
    productTitle: z.string(),
    parentSetupPacketId: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    study: z
        .object({
        studyId: z.string(),
        /** Optional for packets whose study link exists before a parent run is created. */
        latestRun: shopifySetupPacketHistoryLatestRunSchema.nullable().optional(),
    })
        .nullable(),
    failure: z
        .object({
        stage: z.string(),
        code: z.string(),
        message: z.string(),
        at: z.string(),
    })
        .optional(),
});
export const shopifySetupPacketHistoryEntrySchema = z.intersection(shopifySetupPacketHistoryEntryBaseSchema, shopifyTreatmentMetadataSchema);
export const shopifySetupPacketListResponseSchema = z.object({
    packets: z.array(shopifySetupPacketHistoryEntrySchema),
    /** False means the bounded server traversal reached its safety limit. */
    historyComplete: z.boolean().optional(),
});
export const shopifySetupPacketStatusLatestRunSchema = z.object({
    id: z.string(),
    status: z.enum(['queued', 'running', 'completed', 'failed', 'cancelled', 'superseded']),
    latest_step_key: z.string().nullable().optional(),
    requested_at: z.string().nullable().optional(),
    started_at: z.string().nullable().optional(),
    finished_at: z.string().nullable().optional(),
    error_code: z.string().nullable().optional(),
    error_message: z.string().nullable().optional(),
    /** Older Web deployments omit this field; Shopify derives readiness when absent. */
    reportReady: z.boolean().optional(),
    /** Publication coverage, never inferred from a terminal task or the presence of scores. */
    completionStatus: z.enum(['completed', 'completed_with_failures']).optional(),
    nodeProgress: z.array(shopifyStudyNodeProgressSchema).optional(),
    arms: z
        .object({
        total: z.number(),
        completed: z.number(),
        running: z.number(),
        failed: z.number(),
        pending: z.number(),
    })
        .optional(),
    task_progress: z
        .object({
        expected: z.number(),
        completed: z.number(),
        active: z.number(),
        waiting: z.number(),
        capacity_waiting: z.number().optional(),
        retrying: z.number().optional(),
        requires_operator_recovery: z.boolean().optional(),
        failed: z.number(),
    })
        .optional(),
    /** Server-derived milestone for a running scored test; absent when its active phase is unknown. */
    stage: z
        .enum([
        'waiting',
        'preparing_shoppers',
        'asking',
        'scoring',
        'following_up',
        'analysing',
        'consolidating',
        'reviewing',
        'writing_report',
    ])
        .optional(),
    /** Exact respondent-by-arm counts when the complete selected-arm manifest is known. */
    surveyProgress: z
        .object({
        completedResponses: z.number().int().nonnegative(),
        plannedResponses: z.number().int().nonnegative(),
    })
        .optional(),
    failed_children: z
        .array(z.object({
        id: z.string().optional(),
        arm_id: z.string().nullable().optional(),
        status: z.string().optional(),
        latest_step_key: z.string().nullable().optional(),
        error_code: z.string().nullable().optional(),
        error_message: z.string().nullable().optional(),
        failure_sample: z
            .object({
            category: z.string().optional(),
            provider: z.string().optional(),
            model: z.string().optional(),
            error: z.string().optional(),
        })
            .optional(),
    }))
        .optional(),
});
/** Runtime contract for the signed setup-packet status response consumed by Shopify polling. */
export const shopifySetupPacketStatusResponseSchema = z.object({
    packetId: z.string(),
    packetStatus: z.string(),
    study: z
        .object({
        studyId: z.string(),
        url: z.string().optional(),
        latestRun: shopifySetupPacketStatusLatestRunSchema.nullable().optional(),
    })
        .nullable(),
    failure: z
        .object({
        stage: z.string(),
        code: z.string(),
        message: z.string(),
        at: z.string(),
    })
        .optional(),
});
//# sourceMappingURL=shopify-setup-packet-list.js.map